/**
 * What the Store prints beside a product, given what this buyer's country
 * actually pays.
 *
 * Moonfall charges tax-EXCLUSIVE (game/utils/stripeCheckoutTax.js), so "$20"
 * is the seller's net and a German player is charged $23.80. An EU consumer
 * offer is supposed to state the total. The server asks Stripe Tax what it
 * will add to each printed amount and hands the answer to
 * `/api/region/availability`; this module is the one place that decides what
 * a card says with it.
 *
 * TWO RULES, both deliberate:
 *
 *   - WHERE NOTHING IS COLLECTED, NOTHING CHANGES. Stripe adds tax only where
 *     an active registration exists, so a country that pays none answers
 *     `collected: false` and every figure and every sentence is the one that
 *     shipped before this existed. That is the owner's requirement, not a
 *     side effect.
 *   - AN AMOUNT THE SERVER DID NOT PRICE FALLS BACK, and takes the note with
 *     it. A card that printed a net figure under "includes 19% VAT" would be
 *     a worse lie than the one this replaces, so the inclusive presentation
 *     is all-or-nothing across the screen.
 */

/** Every price the Store can print must be covered, or none of them are. */
export function usableDisplayTax(tax, amountsMinor) {
  if (!tax || tax.collected !== true) return null;
  const amounts = tax.amounts;
  if (!amounts || typeof amounts !== 'object') return null;
  for (const amountMinor of amountsMinor) {
    const total = amounts[String(amountMinor)];
    if (!Number.isSafeInteger(total) || total < amountMinor) return null;
  }
  return tax;
}

/** The total this buyer pays for `amountMinor`, or the amount itself. */
export function displayTotalMinor(amountMinor, tax) {
  if (!Number.isSafeInteger(amountMinor)) return amountMinor;
  if (!tax || tax.collected !== true) return amountMinor;
  const total = tax.amounts?.[String(amountMinor)];
  return Number.isSafeInteger(total) && total >= amountMinor ? total : amountMinor;
}

/** True when the figures on screen already contain the buyer's tax. */
export function showsTaxInclusive(tax) {
  return Boolean(tax && tax.collected === true);
}

/**
 * The sentence under the price.
 *
 * Inclusive copy names the rate, because "$20" becoming "$23.80" between two
 * visits to the same page reads as a price rise unless the page says why.
 * VAT is named where Stripe says the tax IS VAT; everywhere else the neutral
 * word is used, so a future GST or JCT registration cannot make the copy lie.
 *
 * @param {object|null} tax
 * @param {(key: string, fallback: string, vars?: object) => string} text
 */
export function taxNoteText(tax, text) {
  if (!showsTaxInclusive(tax)) {
    return text('store.taxNotice', 'Excludes tax, added at checkout.');
  }
  const percent = tax.percentage;
  return tax.taxType === 'vat'
    ? text('store.taxIncludedVat', 'Includes {{percent}}% VAT.', { percent })
    : text('store.taxIncluded', 'Includes {{percent}}% tax.', { percent });
}
